"use client";

import * as React from "react";
import { useActionState } from "react";
import { Button } from "@modernatc/ui/button";
import { Input } from "@modernatc/ui/input";
import { Label } from "@modernatc/ui/label";
import { joinWaitlist, type WaitlistResult } from "@/app/actions/waitlist";

interface WaitlistFormProps {
  source: string;
  className?: string;
}

export function WaitlistForm({ source, className }: WaitlistFormProps) {
  // React 19's `useActionState` gives us `state`, the form action to
  // bind, and `isPending` in one hook. Initial state is `null` (no
  // result yet); we can't import a const for it because `"use server"`
  // files only allow async function exports.
  const [state, formAction, isPending] = useActionState<WaitlistResult | null, FormData>(
    joinWaitlist,
    null,
  );

  if (state?.ok) {
    return (
      <p
        role="status"
        className="text-callsign text-xs text-[var(--color-clear)]"
      >
        ▸ Confirmed. We&rsquo;ll send the launch invite to your inbox.
      </p>
    );
  }

  return (
    <form
      action={formAction}
      className={`flex flex-col gap-3 sm:flex-row sm:items-center ${className ?? ""}`}
    >
      <input type="hidden" name="source" value={source} />
      <div className="flex-1">
        <Label htmlFor={`waitlist-${source}`} className="sr-only">
          Email
        </Label>
        <Input
          id={`waitlist-${source}`}
          name="email"
          type="email"
          placeholder="callsign@example.com"
          required
          autoComplete="email"
          aria-invalid={state && !state.ok ? true : undefined}
        />
      </div>
      <Button type="submit" variant="secondary" disabled={isPending}>
        {isPending ? "Sending…" : "Notify me"}
      </Button>
      {state && !state.ok ? (
        <p className="text-xs text-[var(--color-critical)]">{state.error}</p>
      ) : null}
    </form>
  );
}
